import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Volume2, VolumeX, Send, ChevronDown } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function LaptopAvatar({ size = 48, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      {/* Graduation cap */}
      <polygon points="60,18 20,38 60,58 100,38" fill="#002FA7" />
      <polygon points="60,58 100,38 100,48 60,68" fill="#001B63" />
      <polygon points="60,58 20,38 20,48 60,68" fill="#0040D0" />
      <rect x="58" y="18" width="4" height="8" fill="#FFC107" />
      <circle cx="60" cy="16" r="3" fill="#FFC107" />
      {/* Tassel */}
      <line x1="96" y1="38" x2="105" y2="52" stroke="#FFC107" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="105" cy="54" r="3" fill="#FFC107" />
      {/* Laptop base */}
      <rect x="25" y="62" width="70" height="42" rx="4" fill="#1E293B" />
      {/* Screen */}
      <rect x="30" y="66" width="60" height="30" rx="2" fill="#60A5FA" />
      {/* Face on screen - eyes */}
      <circle cx="47" cy="78" r="3.5" fill="#1E293B" />
      <circle cx="73" cy="78" r="3.5" fill="#1E293B" />
      {/* Eye glints */}
      <circle cx="48.5" cy="77" r="1.2" fill="white" />
      <circle cx="74.5" cy="77" r="1.2" fill="white" />
      {/* Smile */}
      <path d="M50 87 Q60 94 70 87" stroke="#1E293B" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      {/* Keyboard base */}
      <path d="M15 104 L25 96 L95 96 L105 104 Z" fill="#334155" />
      <rect x="15" y="104" width="90" height="4" rx="2" fill="#475569" />
    </svg>
  );
}

const GREETING = "Hey there! I'm RikirBot — your virtual guide to RikirTutor. Ask me anything about our programs, pricing, or how to get started!";

const QUICK_QUESTIONS = [
  "What programs do you offer?",
  "How much does it cost?",
  "How do I book a session?",
  "What's included in the Star Package?",
];

export default function SiteAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: GREETING }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `v-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [hasInteracted, setHasInteracted] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Offline fallback answers when API is unreachable
  const getOfflineAnswer = (text) => {
    const q = text.toLowerCase();
    if (q.includes('star package') || q.includes('star plan') || q.includes('775'))
      return "The Star Package is $775 for 2 full months of unlimited access — that's unlimited 1-on-1 sessions, group sessions, the full video library, AI study tools, and priority booking. You save $225 compared to buying 2 months separately! Check it out at /pricing.";
    if (q.includes('price') || q.includes('cost') || q.includes('plan') || q.includes('package') || q.includes('how much'))
      return "We have flexible plans: Single Class ($15), Group Session ($20), 5-Day Bundle ($150, save $25), Weekly ($100/week — 3 sessions + unlimited groups + platform), Monthly Unlimited ($500/month), and Star Package ($775/2 months, best savings). See all details at /pricing!";
    if (q.includes('ace') || q.includes('tutor') || q.includes('teacher') || q.includes('instructor'))
      return "Ace Black is the founder of RikirTutor with 15+ years of teaching experience and a 95% student pass rate. He's certified in WorkKeys, GED, SAT, and is a National Tutoring Association member. He teaches live 1-on-1 and group sessions across all programs.";
    if (q.includes('ged'))
      return "Our GED program covers Math, Science, English/Language Arts, and Social Studies. Classes run Mon-Fri from 8-10 AM. We have video lessons, quizzes, worksheets, and AI practice tools for each subject. Visit /schedule for the full timetable!";
    if (q.includes('sat'))
      return "SAT Prep covers Math, Reading, and Writing & Language. Classes run Mon-Fri from 12-2 PM. We have practice tests, video lessons, and an AI question generator to help you boost your score. Check /schedule for details!";
    if (q.includes('workkeys'))
      return "WorkKeys Certification Prep covers Applied Mathematics, Workplace Documents, Graphic Literacy, and Business Writing. Classes run Mon-Fri from 10 AM-12 PM. Full video library and practice tests included with platform access!";
    if (q.includes('college'))
      return "College courses include Algebra, Chemistry, Biology, Business, and Writing. Classes run Mon-Sat from 6-10 PM. Video lessons and quizzes are available for each subject. See /schedule for the full breakdown!";
    if (q.includes('program') || q.includes('subject') || q.includes('course') || q.includes('offer') || q.includes('what do'))
      return "We offer 4 programs: GED Prep (Math, Science, English, Social Studies), SAT Prep (Math, Reading, Writing), WorkKeys Certification (Applied Math, Documents, Graphic Literacy, Business Writing), and College Courses (Algebra, Chemistry, Biology, Business, Writing). Visit /schedule for class times!";
    if (q.includes('book') || q.includes('session') || q.includes('schedule') || q.includes('appointment'))
      return "To book a session: 1) Create a free account at /register, 2) Pick a plan at /pricing, 3) After purchase, you'll land on the Book Session tab in your dashboard where you can choose a date, time, and subject. You'll get an email reminder 24 hours before!";
    if (q.includes('start') || q.includes('begin') || q.includes('sign up') || q.includes('register') || q.includes('join'))
      return "Getting started is easy! Create a free account at /register, then choose a plan at /pricing. After purchase, you'll be taken straight to your dashboard to book your first session. We also have free sample lessons on the homepage!";
    if (q.includes('video') || q.includes('lesson') || q.includes('library'))
      return "Our video library has 62+ lessons covering GED, SAT, WorkKeys, and College courses. You can watch free preview clips on our homepage. Full access comes with the Weekly ($100/wk), Monthly ($500/mo), or Star ($775/2mo) plans.";
    if (q.includes('ai') || q.includes('chat') || q.includes('practice') || q.includes('quiz'))
      return "We have an AI Study Assistant that can answer any subject question 24/7, plus an AI Practice Question Generator that creates custom quizzes. These are available with Weekly, Monthly, or Star plans. We also have pre-built quizzes and worksheets for every module!";
    if (q.includes('cancel') || q.includes('refund'))
      return "Yes, you can cancel your subscription anytime from your dashboard. Your access continues until the end of your billing period. If you don't improve, your next session is free — that's our guarantee!";
    if (q.includes('contact') || q.includes('email') || q.includes('help') || q.includes('support'))
      return "You can reach us through the Contact page at /contact, or email directly at acerikir1@gmail.com. We're happy to answer any questions!";
    if (q.includes('free') || q.includes('trial') || q.includes('sample'))
      return "We have free sample lessons on our homepage — no account needed! You can also get a free personalized study plan by entering your email in the 'Get Your Free Study Plan' section on the homepage. Single classes start at just $15 if you want to try a live session.";
    if (q.includes('guarantee') || q.includes('improve') || q.includes('money back'))
      return "We stand behind our teaching. If you don't improve, your next session is free — that's our guarantee. Ace has a 95% pass rate, so we're confident you'll see results!";
    return "Great question! Here's a quick overview: We offer GED, SAT, WorkKeys, and College tutoring starting at $15/session. Visit /pricing to see all plans, /schedule for class times, or /register to create a free account. You can also email us at acerikir1@gmail.com!";
  };

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    setHasInteracted(true);
    const userMsg = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    const attemptSend = async (retries = 1) => {
      try {
        const { data } = await axios.post(`${API_URL}/api/assistant/chat`, {
          message: text.trim(),
          session_id: sessionId,
        });
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } catch (err) {
        if (retries > 0) {
          await new Promise(r => setTimeout(r, 1500));
          return attemptSend(retries - 1);
        }
        // Use offline fallback when API is unreachable
        const fallback = getOfflineAnswer(text);
        setMessages(prev => [...prev, { role: 'assistant', content: fallback }]);
      }
    };

    await attemptSend(1);
    setLoading(false);
  }, [loading, sessionId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  // Dismissed completely — show small bring-back button
  if (isDismissed) {
    return (
      <button
        onClick={() => { setIsDismissed(false); setIsOpen(true); }}
        className="fixed bottom-4 right-4 z-50 bg-[#002FA7] hover:bg-[#001B63] text-white p-2.5 rounded-full shadow-lg transition-all hover:scale-110"
        data-testid="assistant-restore-btn"
        title="Bring back RikirBot"
      >
        <LaptopAvatar size={28} />
      </button>
    );
  }

  // Muted — show floating avatar only (no chat)
  if (isMuted && !isOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
        <button
          onClick={() => setIsOpen(true)}
          className="relative group"
          data-testid="assistant-muted-btn"
        >
          <div className="w-14 h-14 bg-[#002FA7] rounded-full shadow-lg flex items-center justify-center transition-all group-hover:scale-110 group-hover:shadow-xl">
            <LaptopAvatar size={36} />
          </div>
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#FFC107] rounded-full flex items-center justify-center">
            <VolumeX className="w-2.5 h-2.5 text-[#0A0A0A]" />
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2" data-testid="site-assistant">
      {/* Chat Window */}
      {isOpen && (
        <div
          className="w-[340px] sm:w-[380px] bg-white rounded-lg shadow-2xl border border-[#E5E7EB] flex flex-col overflow-hidden"
          style={{ maxHeight: 'min(520px, calc(100vh - 100px))' }}
          data-testid="assistant-chat-window"
        >
          {/* Header */}
          <div className="bg-[#002FA7] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <LaptopAvatar size={32} />
              <div>
                <p className="text-white font-bold text-sm leading-tight">RikirBot</p>
                <p className="text-white/60 text-[10px]">Your virtual guide</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setIsMuted(true); setIsOpen(false); }}
                className="p-1.5 rounded hover:bg-white/10 transition-colors"
                title="Mute assistant"
                data-testid="assistant-mute-btn"
              >
                <VolumeX className="w-4 h-4 text-white/80" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded hover:bg-white/10 transition-colors"
                title="Minimize"
                data-testid="assistant-minimize-btn"
              >
                <ChevronDown className="w-4 h-4 text-white/80" />
              </button>
              <button
                onClick={() => { setIsDismissed(true); setIsOpen(false); }}
                className="p-1.5 rounded hover:bg-white/10 transition-colors"
                title="Dismiss assistant"
                data-testid="assistant-dismiss-btn"
              >
                <X className="w-4 h-4 text-white/80" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F9FAFB]" style={{ minHeight: '200px' }}>
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-[#002FA7] flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                    <LaptopAvatar size={18} />
                  </div>
                )}
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-lg text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#002FA7] text-white rounded-br-none'
                      : 'bg-white border border-[#E5E7EB] text-[#1F2937] rounded-bl-none shadow-sm'
                  }`}
                  data-testid={`assistant-msg-${i}`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="w-7 h-7 rounded-full bg-[#002FA7] flex items-center justify-center mr-2 flex-shrink-0">
                  <LaptopAvatar size={18} />
                </div>
                <div className="bg-white border border-[#E5E7EB] px-4 py-2.5 rounded-lg rounded-bl-none shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Quick Questions (only before interaction) */}
          {!hasInteracted && (
            <div className="px-3 py-2 border-t border-[#E5E7EB] bg-white flex-shrink-0">
              <div className="flex flex-wrap gap-1.5">
                {QUICK_QUESTIONS.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    className="text-xs bg-[#EFF6FF] text-[#002FA7] px-2.5 py-1.5 rounded-full hover:bg-[#DBEAFE] transition-colors font-medium"
                    data-testid={`quick-q-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSubmit} className="flex items-center gap-2 p-3 border-t border-[#E5E7EB] bg-white flex-shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything..."
              className="flex-1 text-sm bg-[#F9FAFB] border border-[#E5E7EB] rounded-full px-4 py-2 focus:outline-none focus:border-[#002FA7] transition-colors"
              disabled={loading}
              data-testid="assistant-input"
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="w-9 h-9 bg-[#002FA7] hover:bg-[#001B63] disabled:bg-[#9CA3AF] text-white rounded-full flex items-center justify-center transition-colors flex-shrink-0"
              data-testid="assistant-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      {/* Floating Avatar Button */}
      {!isOpen && !isMuted && (
        <button
          onClick={() => setIsOpen(true)}
          className="group relative"
          data-testid="assistant-open-btn"
        >
          <div className="w-16 h-16 bg-[#002FA7] rounded-full shadow-lg flex items-center justify-center transition-all group-hover:scale-110 group-hover:shadow-xl group-hover:bg-[#001B63]">
            <LaptopAvatar size={42} />
          </div>
          <span className="absolute -top-1 -left-1 w-5 h-5 bg-[#FFC107] rounded-full flex items-center justify-center text-[10px] font-bold text-[#0A0A0A] animate-pulse">?</span>
        </button>
      )}
    </div>
  );
}
